import { useEffect, useState } from "react";
import { Badge, Tabs, Tag } from "antd";
import {
  FlagOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import ProjectInformation from "./ProjectInformationById";
import ProjectQuickViewCard, {
  ProjectHeaderMeta,
  getStatusColor,
} from "./ProjectQuickViewCard";
import ProjectPageShell, {
  useProjectContext,
} from "../shared/ProjectPageShell";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import TopMenu from "../../../route/topMenu";
import { useLocation, useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { CommentsTab } from "../../../components/comments/Comments";
import ActivityStreamLine from "../activityStreamLine/ActivityStreamLine";
import StatusTimeLine from "../statusTimeLine/StatusTimeLine";
import { projectsApis } from "../../../api/projects";
import { ApiResponse } from "../../../api/master/axiosIndex";

interface TabItem {
  label: string;
  tabKey: string;
  content: React.ReactNode;
}

const ProjectDetailsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId: routeProjectId } = useParams<{ projectId: string }>();
  const {
    id: stateId,
    derivedProjectStatus,
    projectName: stateProjectName,
    extra,
  } = location.state || {};
  // Resolve the project from router state, the /view/:projectId URL param and
  // the Redux layout fallback — so a hard refresh on this URL still renders.
  const { projectId: ctxProjectId, projectName: ctxProjectName } =
    useProjectContext();
  const id =
    stateId ||
    (routeProjectId && !isNaN(Number(routeProjectId))
      ? Number(routeProjectId)
      : null) ||
    ctxProjectId;
  const projectName = stateProjectName || ctxProjectName;
  const dispatch = useDispatch();
  const [tab, setTab] = useState("view");
  const [viewMode, setViewMode] = useState<"summary" | "edit">("summary");
  const [transitions, setTransitions] = useState([]);
  const [activityStreamLineData, setActivityStreamLineData] = useState<any>();
  // Status/flag chips shown beside the page title (reported by the quick view)
  const [headerMeta, setHeaderMeta] = useState<ProjectHeaderMeta | null>(null);

  // Reset to summary when project id changes
  useEffect(() => {
    setViewMode("summary");
    setHeaderMeta(null);
  }, [id]);

  // Scroll to top when component mounts or when navigating to this page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (id) {
      getStatusTimeLineData();
      getActivityStreamData();
    }
  }, [id]);

  const getStatusTimeLineData = async () => {
    if (!id) {
      console.warn("Project ID is not available for status timeline");
      return;
    }
    try {
      const apiResponse: ApiResponse = await projectsApis.fetchStatusTimeline(
        id,
        id
      );
      const { data, status } = apiResponse;
      if (status) {
        setTransitions(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getActivityStreamData = async () => {
    if (!id) {
      console.warn("Project ID is not available for activity stream");
      return;
    }
    try {
      const apiResponse: ApiResponse =
        await projectsApis.fetchActivityStreamlines(id, id);
      const { data, status } = apiResponse;
      if (status) {
        setActivityStreamLineData(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    dispatch(
      updateMenuData(
        TopMenu?.projectNav?.map((item: any) => {
          return {
            ...item,
            linkState: location.state,
          };
        })
      )
    );
  }, [location]);

  const tabs: TabItem[] = [
    {
      label: "View",
      tabKey: "view",
      content:
        viewMode === "summary" ? (
          <ProjectQuickViewCard
            projectId={id}
            derivedProjectStatus={derivedProjectStatus}
            onEdit={() => setViewMode("edit")}
            onProjectMeta={setHeaderMeta}
          />
        ) : (
          <ProjectInformation
            isViewMode={true}
            initialEditMode={true}
            onBackToSummary={() => setViewMode("summary")}
            ProjectInDetailData={{
              id,
              derivedProjectStatus,
              projectName,
              extra,
            }}
          />
        ),
    },
    {
      label: "Comments",
      tabKey: "comments",
      content: (
        <CommentsTab
          module="Project"
          moduleId={id}
          projectId={id}
          name={projectName}
          projectName={projectName}
        />
      ),
    },
    {
      label: "Timeline",
      tabKey: "timeline",
      content: <StatusTimeLine transitions={transitions} />,
    },
    {
      label: "Activity",
      tabKey: "activity",
      content: (
        <ActivityStreamLine groupedActivities={activityStreamLineData} />
      ),
    },
  ];

  const handleBackToProjects = () => {
    navigate("/pms/projects");
  };

  return (
    <ProjectPageShell
      headTitle={`${projectName || "Project"} - Dashboard`}
      breadcrumb={[
        { label: "Projects", onClick: handleBackToProjects },
        { label: projectName || "Project" },
      ]}
      title={
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          {projectName || "Project"}
          {headerMeta?.status && (
            <Badge
              status={getStatusColor(headerMeta.status)}
              text={
                <span style={{ fontWeight: 500, fontSize: 13 }}>
                  {headerMeta.status}
                </span>
              }
            />
          )}
          {headerMeta?.lock && (
            <Tag color="orange" icon={<LockOutlined />} style={{ margin: 0 }}>
              Locked
            </Tag>
          )}
          {headerMeta?.isPrivate && (
            <Tag color="purple" icon={<LockOutlined />} style={{ margin: 0 }}>
              Private
            </Tag>
          )}
          {headerMeta?.isStrict && (
            <Tag color="red" icon={<FlagOutlined />} style={{ margin: 0 }}>
              Strict
            </Tag>
          )}
          {headerMeta?.approvalRequired === false && (
            <Tag
              color="gold"
              icon={<SafetyCertificateOutlined />}
              style={{ margin: 0 }}
            >
              No Approval
            </Tag>
          )}
        </span>
      }
      eyebrow="Project"
      onBack={handleBackToProjects}
    >
      {/* White surface holding the tabs + content (matches the projects hub) */}
      <div className="pms-hub-surface">
        <Tabs
          className="pms-tabs"
          activeKey={tab}
          onChange={(key) => setTab(key)}
          // The legacy nav rendered only the active tab's content — inactive
          // tabs were unmounted on switch. destroyInactiveTabPane keeps those
          // mount/unmount semantics (mount on activate, unmount on leave).
          destroyInactiveTabPane
          // ProjectQuickViewCard portals its Edit/actions buttons into this
          // slot so they sit right-aligned on the tabs row.
          tabBarExtraContent={{
            right: (
              <div
                id="project-view-actions"
                style={{ display: "flex", alignItems: "center" }}
              />
            ),
          }}
          items={tabs.map((tabItem) => ({
            key: tabItem.tabKey,
            label: tabItem.label,
            children: tabItem.content,
          }))}
        />
      </div>
    </ProjectPageShell>
  );
};

export default ProjectDetailsView;
